import { useEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * A thin top progress bar that animates during route transitions.
 * Inspired by NProgress — pure CSS, no dependencies.
 */
export default function RouteProgressBar() {
  const location = useLocation();
  const [active, setActive] = useState(false);
  const [width, setWidth] = useState(0);
  const [visible, setVisible] = useState(false);
  const prevKey = useRef(location.key);
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);

  const clear = () => {
    timers.current.forEach(clearTimeout);
    timers.current = [];
  };

  useEffect(() => {
    if (location.key === prevKey.current) return;
    prevKey.current = location.key;

    clear();
    // Start: appear and race to ~80%
    setVisible(true);
    setWidth(0);
    setActive(true);

    const t1 = setTimeout(() => setWidth(35), 20);
    const t2 = setTimeout(() => setWidth(65), 180);
    const t3 = setTimeout(() => setWidth(82), 380);

    // Complete: shoot to 100% and fade out
    const t4 = setTimeout(() => {
      setWidth(100);
      setActive(false);
    }, 500);

    const t5 = setTimeout(() => {
      setVisible(false);
      setWidth(0);
    }, 780);

    timers.current = [t1, t2, t3, t4, t5];
    return clear;
  }, [location.key]);

  if (!visible) return null;

  return (
    <div
      className="fixed top-0 left-0 w-full z-[200] pointer-events-none"
      aria-hidden="true"
    >
      <div
        className="h-[2px] rounded-r-full"
        style={{
          width: `${width}%`,
          background: 'linear-gradient(90deg, #18c7f4, #384774)',
          transition: active
            ? 'width 300ms cubic-bezier(0.4, 0, 0.2, 1)'
            : 'width 200ms ease-out',
          boxShadow: '0 0 8px rgba(24, 199, 244, 0.6)',
        }}
      />
      {/* Glow tip */}
      {width > 0 && width < 100 && (
        <div
          className="absolute top-0 w-5 h-[2px] rounded-full"
          style={{
            left: `calc(${width}% - 10px)`,
            background: 'radial-gradient(circle, rgba(24,199,244,0.9) 0%, transparent 70%)',
            transition: active
              ? 'left 300ms cubic-bezier(0.4, 0, 0.2, 1)'
              : 'left 200ms ease-out',
          }}
        />
      )}
    </div>
  );
}